import CustomError from "models/CustomError"
import { IValidationRule } from "@/types"
import { INT32_MAX_VALUE } from "config/constants"
import articleValidationRules from "validation/articleValidationRules"
import assetValidationRules from "validation/assetValidationRules"
import reservationValidationRules from "validation/reservationValidationRules"
import invoiceValidationRules from "validation/invoiceValidationRules"
import financialFactorsValidationRules from "validation/financialFactorsValidationRules"
import overheadFactorsValidationRules from "validation/overheadFactorsValidationRules"
import collectiveReferenceNumbersRules from "validation/collectiveReferenceNumbersRules"
import costCategoryValidationRules from "validation/costCategoryValidationRules"
import invoiceOutcomingValidationRules from "validation/invoiceOutcomingValidationRules"
import { getDecimalSeparator, isValid6CharHexColor, stringToNumber } from "utils/utils"
import depreciationPeriodValidationRules from "validation/depreciationPeriodValidationRules"
import depreciationPeriodIfrsValidationRules from "validation/depreciationPeriodIfrsValidationRules"
import labelCategoryValidationRules from "validation/labelCategoryValidationRules"
import documentCategoryValidationRules from "validation/documentCategoryValidationRules"
import assetCategoryValidationRules from "validation/assetCategoryValidationRules"
import assetTypeValidationRules from "validation/assetTypeValidationRules"
import transportValidationRules from "validation/transportValidationRules"
import locationValidationRules from "validation/locationValidationRules"
import tenantContactsValidationRules from "validation/tenantContactsValidationRules"
import tenantEmployeeValidationRules from "validation/tenantEmployeeValidationRules"
import tenantWarehouseValidationRules from "validation/tenantWarehouseValidationRules"
import otherShippingDetailsValidationRules from "validation/otherShippingDetailsValidationRules"
import deliveryTermValidationRules from "validation/deliveryTermValidationRules"
import shippingOrderValidationRules from "validation/shippingOrderValidationRules"
import evaluationParameterValidationRules from "validation/evaluationParameterValidationRules"
import textBuildingBlockValidationRules from "validation/textBuildingBlockValidationRules"
import assetDisposalValidationRules from "validation/assetDisposalValidationRules"
import invoicePositionValidationRules from "validation/invoicePositionValidationRules"
import downtimePeriodValidationRules from "validation/downtimePeriodValidationRules"
import specialDepreciationValidationRules from "validation/specialDepreciationValidationRules"
import stocktakingOrderValidationRules from "validation/stocktakingOrderValidationRules"
import taxBasedDeductionValidationRules from "validation/taxBasedDeductionValidationRules"
import assetEndHoldershipValidationRules from "validation/assetEndHoldershipValidationRules"
import orderTypeValidationRules from "validation/orderTypeValidationRules"
import shippingOrderCartManualPositionValidationRules from "validation/shippingOrderCartManualPositionValidationRules"
import colorValidationRules from "validation/colorValidationRules"
import offerValidationRules from "validation/offerValidationRules"
import freightUnitValidationRules from "validation/freightUnitValidationRules"
import fairValueFactorsValidationRules from "validation/fairValueFactorsRules"
import depreciationMatrixValidationRules from "validation/depreciationMatrixRules"

export const validationRules = {
    article: articleValidationRules,
    asset: assetValidationRules,
    reservation: reservationValidationRules,
    offer: offerValidationRules,
    invoice: invoiceValidationRules,
    financialFactors: financialFactorsValidationRules,
    overheadFactors: overheadFactorsValidationRules,
    collectiveReferenceNumbers: collectiveReferenceNumbersRules,
    tenantContacts: tenantContactsValidationRules,
    tenantEmployee: tenantEmployeeValidationRules,
    tenantWarehouse: tenantWarehouseValidationRules,
    otherShippingDetails: otherShippingDetailsValidationRules,
    deliveryTerm: deliveryTermValidationRules,
    invoiceOutcoming: invoiceOutcomingValidationRules,
    costCategory: costCategoryValidationRules,
    labelCategory: labelCategoryValidationRules,
    documentCategory: documentCategoryValidationRules,
    assetCategory: assetCategoryValidationRules,
    assetType: assetTypeValidationRules,
    depreciationPeriod: depreciationPeriodValidationRules,
    depreciationPeriodIfrs: depreciationPeriodIfrsValidationRules,
    depreciationMatrix: depreciationMatrixValidationRules,
    evaluationParameter: evaluationParameterValidationRules,
    transport: transportValidationRules,
    shippingOrderCartManualPosition: shippingOrderCartManualPositionValidationRules,
    shippingOrder: shippingOrderValidationRules,
    location: locationValidationRules,
    textBuildingBlock: textBuildingBlockValidationRules,
    assetDisposal: assetDisposalValidationRules,
    assetEndHoldership: assetEndHoldershipValidationRules,
    invoicePosition: invoicePositionValidationRules,
    orderType: orderTypeValidationRules,
    downtimePeriod: downtimePeriodValidationRules,
    specialDepreciation: specialDepreciationValidationRules,
    stocktakingOrder: stocktakingOrderValidationRules,
    fairValueFactors: fairValueFactorsValidationRules,
    taxBasedDeduction: taxBasedDeductionValidationRules,
    color: colorValidationRules,
    freightUnit: freightUnitValidationRules,
}

class ValidationService {
    static validate(rules: IValidationRule, value: any): CustomError | null {
        if (rules.required === true && (typeof value === "undefined" || value === null || value === "" || (typeof value === "string" ? value.trim() === "" : false))) {
            return new CustomError({
                messageCode: "validation.required"
            })
        }
        if (rules.requiredRadio === true && (typeof value === "undefined" || value === null || value === "")) {
            return new CustomError({
                messageCode: "validation.requiredRadio"
            })
        }

        if (rules.requiredSlider === true && (typeof value === "undefined" || value === null || value === "")) {
            return new CustomError({
                messageCode: "validation.requiredSlider"
            })
        }

        if (rules.arrayNotEmpty === true && (!value || (value instanceof Array && value.length === 0))) {
            return new CustomError({
                messageCode: "validation.arrayNotEmpty"
            })
        }

        if (!value) {
            return null
        }

        if (rules.arrayNotEmpty
            && (!(value instanceof Array) || value.length === 0)) {
            return new CustomError({
                messageCode: "validation.arrayNotEmpty"
            })
        }

        if (rules.notEqual
            && (
                rules.notEqual instanceof Array
                    ? rules.notEqual.map(item => typeof item === "string" ? item.trim() : item).includes(typeof value === "string" ? value.trim() : value)
                    : (typeof value === "string" && typeof rules.notEqual === "string" ? value.trim() === rules.notEqual.trim() : value === rules.notEqual)
            )) {
            return new CustomError({
                messageCode: "validation.notEqual"
            })
        }

        if (rules.type === "decimal") {
            const floatValue = stringToNumber(value)
            const decimalSeparator = getDecimalSeparator()

            if (!(new RegExp(`^-?\\d+\\${decimalSeparator}?\\d*$`).test(value)) || value[value.length - 1] === decimalSeparator || floatValue === null) {
                return new CustomError({
                    messageCode: "validation.decimal",
                    options: { decimalSeparator }
                })
            }

            if (rules.nonZero && floatValue === 0) {
                return new CustomError({
                    messageCode: "validation.nonZero"
                })
            }
        }

        if (rules.type === "integer") {
            const floatValue = stringToNumber(value)

            if (!(/^-?\d+$/.test(value)) || floatValue === null) {
                return new CustomError({
                    messageCode: "validation.integer"
                })
            }

            if (rules.nonZero && floatValue === 0) {
                return new CustomError({
                    messageCode: "validation.nonZero"
                })
            }
        }

        if (rules.type === "integer" || rules.type === 'decimal') {
            const floatValue = stringToNumber(value)
            if (floatValue !== null) {
                if (typeof rules.gt !== "undefined" && floatValue <= rules.gt) {
                    return new CustomError({
                        messageCode: "validation.greaterThan",
                        options: {
                            gt: rules.gt
                        }
                    })
                }
                if (typeof rules.gte !== "undefined" && floatValue < rules.gte) {
                    return new CustomError({
                        messageCode: "validation.greaterThanOrEqual",
                        options: {
                            gte: rules.gte
                        }
                    })
                }
            }
        }

        if (rules.type === "integer" || rules.type === "decimal") {
            const floatValue = stringToNumber(value)
            if (floatValue !== null) {
                if ((typeof rules.min !== "undefined" && floatValue < rules.min)
                    || (floatValue < (typeof rules.min !== "undefined" ? rules.min : -INT32_MAX_VALUE))
                    || (floatValue > (typeof rules.max !== "undefined" ? rules.max : INT32_MAX_VALUE))) {
                    return new CustomError({
                        messageCode: "validation.minMax",
                        options: {
                            min: typeof rules.min !== "undefined" ? rules.min : -INT32_MAX_VALUE,
                            max: typeof rules.max !== "undefined" ? rules.max : INT32_MAX_VALUE
                        }
                    })
                }
            }
        }

        if (rules.type === "email"
            && (!(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value)))) {
            return new CustomError({
                messageCode: "validation.email"
            })
        }

        if (rules.type === "alphanumeric"
            && (!(/^[a-zA-Z0-9]+$/.test(value)))) {
            return new CustomError({
                messageCode: "validation.alphanumeric"
            })
        }

        if (rules.type === "hexColor6Char"
            && !isValid6CharHexColor(value)) {
            return new CustomError({
                messageCode: "validation.hexColor6Char"
            })
        }

        return null
    }
    static validateMultiple<Key extends string>(items: [Key, IValidationRule, any][]): Record<Key, CustomError> {
        const result = {} as Record<Key, CustomError>
        items.forEach(item => {
            const itemResult = this.validate(item[1], item[2])
            if (itemResult !== null) {
                result[item[0]] = itemResult
            }
        })
        return result
    }
    static isFieldInvalid(errors: { [x: string]: CustomError }, key: string): boolean {
        return Boolean(errors && errors[key])
    }

    static isSomeOfFieldsInvalid(errors: { [x: string]: CustomError }, keys: string[]): boolean {
        return errors && keys.some(key => errors[key])
    }

    static hasFieldErrorMessage(errors: { [x: string]: CustomError }, key: string): boolean {
        return Boolean(this.isFieldInvalid(errors, key) && errors[key].message)
    }
}

export default ValidationService
